import React, { MouseEventHandler, ReactNode } from 'react'
import { Button, CircularProgress } from '@mui/material'
import { useTheme } from '@mui/material/styles'

export enum WidgetButtonType {
  Cta = 'cta',
  Nav = 'nav',
  Action = 'action',
}

export interface WidgetButtonProps {
  icon?: ReactNode
  label: string
  action?: MouseEventHandler<HTMLButtonElement>
  type?: WidgetButtonType
  isSubmitting?: boolean
  isLoading?: boolean
}

const WidgetButton = (props: WidgetButtonProps) => {
  const theme = useTheme()
  const buttonStyle = {
    cta: {
      textTransform: 'initial',
      width: '100%',
      padding: theme.spacing(2.125),
      borderRadius: '12px',
      boxShadow: '0px 3px 2px 0px #FFFFFF26 inset, 0px 2px 6px 0px #00000026',
      backgroundColor: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      gap: Boolean(props.icon) ? '5px' : 'initial',
      height: theme.spacing(6.5),
      '&.MuiButton-contained': {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 0.889,
      },
      '&.MuiButton-root:hover': {
        backgroundColor: theme.palette.primary.main,
        opacity: 0.8,
        boxShadow: '0px 3px 2px 0px #FFFFFF26 inset, 0px 2px 6px 0px #00000026',
        transition: '0.15s',
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.primary.light,
        opacity: 0.4,
        color: 'white',
      },
    },
    nav: {
      textTransform: 'lowercase',
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.125,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      color: 'rgba(0, 0, 0, 0.4)',
      visibility: Boolean(props.action) ? 'initial' : 'hidden',
    },
    action: {
      textTransform: 'lowercase',
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.125,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      color: theme.palette.primary.main,
      opacity: 0.75,
    },
  }
  let styles: object
  switch (props.type) {
    case WidgetButtonType.Action:
      styles = buttonStyle.action
      break
    case WidgetButtonType.Nav:
      styles = buttonStyle.nav
      break
    default:
      styles = buttonStyle.cta
  }
  return (
    <Button
      sx={{
        padding: 0,
        minWidth: 'initial',
        ...styles,
      }}
      onClick={props.action}
      color={props.type === WidgetButtonType.Cta ? 'primary' : undefined}
      variant={props.type === WidgetButtonType.Cta ? 'contained' : 'text'}
      disabled={props.isSubmitting}
    >
      {props.isLoading ? (
        <CircularProgress size='1.4rem' color='inherit' />
      ) : (
        <>
          {props.icon}
          {props.label}
        </>
      )}
    </Button>
  )
}
export default WidgetButton
